import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import queryString from 'query-string';
import { useLocation, useNavigate } from 'react-router-dom';
import { CREATED_AT_KEY, DESC } from 'common/constants/analyst-dashboards';
import { useLocalStorageByUser } from 'common/hooks';
import { LoadingSection } from 'components';
import { ATTIO_CURRENT_VALUATIONS_FILTER, ATTIO_OPPORTUNITIES_FILTER, ATTIO_PAST_VALUATIONS_FILTER } from 'dashboard409a/common/constants/analyst-dashboard';
import { VALUATIONS_VIEW_KEY } from 'dashboard409a/common/constants/calendar';
import { CURRENT_VALUATIONS_SLUG, OPPORTUNITIES_SLUG, PAST_VALUATIONS_SLUG } from 'dashboard409a/common/constants/urls';
import { OperationsModal } from 'dashboard409a/components/OperationsModal';
import ValuationsCalendarWithToolbar from 'dashboard409a/components/ValuationsCalendar/ValuationsCalendarWithToolbar';
import { GenericWorkboard } from 'dashboard409a/components/ValuationsTable/common';
import { ValuationsTableBulkData } from 'dashboard409a/components/ValuationsTable/ValuationsTableBulkData';
import ViewToggle, { ViewType } from 'dashboard409a/components/ViewToggle/ViewToggle';
import { useMultiColumnSort } from 'dashboard409a/hooks/useMultiColumnSort';
import { SimplifiedAttioValuation } from 'dashboard409a/services/attio.types';
import { useFindSlug } from 'dashboard409a/utilities';

const VALUATIONS_SORT_KEY = 'valuationsSort';

const WorkboardDataTable = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const currentSlug = useFindSlug();

  // Determine sectionId based on the current route
  const sectionId = useMemo(() => {
    if (currentSlug === CURRENT_VALUATIONS_SLUG) {
      return CURRENT_VALUATIONS_SLUG;
    }
    if (currentSlug === PAST_VALUATIONS_SLUG) {
      return PAST_VALUATIONS_SLUG;
    }
    if (currentSlug === OPPORTUNITIES_SLUG) {
      return OPPORTUNITIES_SLUG;
    }
    return undefined;
  }, [currentSlug]);

  // Use shared multi-column sort hook - default sort is by created_at (creation date)
  const { sortCriteria, orderBy, order, isReady: isSortReady, handleRequestSort } = useMultiColumnSort({
    storageKey: VALUATIONS_SORT_KEY,
    defaultOrderBy: CREATED_AT_KEY,
    defaultOrder: DESC,
  });

  const filters = useMemo(() => {
    if (currentSlug === CURRENT_VALUATIONS_SLUG) {
      return ATTIO_CURRENT_VALUATIONS_FILTER;
    }
    if (currentSlug === PAST_VALUATIONS_SLUG) {
      return ATTIO_PAST_VALUATIONS_FILTER;
    }
    if (currentSlug === OPPORTUNITIES_SLUG) {
      return ATTIO_OPPORTUNITIES_FILTER;
    }
    return ATTIO_CURRENT_VALUATIONS_FILTER;
  }, [currentSlug]);

  const isCalendarSupportedSection = currentSlug === CURRENT_VALUATIONS_SLUG || currentSlug === PAST_VALUATIONS_SLUG;
  const [storedView, setStoredView, isViewReady] = useLocalStorageByUser<ViewType>(
    VALUATIONS_VIEW_KEY,
    'table'
  );
  const view: ViewType = storedView === 'calendar' || storedView === 'table' ? storedView : 'table';

  const [openModal, setOpenModal] = useState(false);
  const [selectedValuation, setSelectedValuation] = useState<SimplifiedAttioValuation | string | undefined>();
  const [pinnedValuation, setPinnedValuation] = useState<SimplifiedAttioValuation | undefined>();
  const [visibleValuations, setVisibleValuations] = useState<SimplifiedAttioValuation[]>([]);

  // Parse query params — valuation-id
  const queryParams = useMemo(() => queryString.parse(location.search) as Record<string, string | undefined>, [location.search]);
  const valuationIdFromUrl = queryParams['valuation-id'];
  const queryParamsRef = useRef(queryParams);
  queryParamsRef.current = queryParams;

  const operationsModalData = useMemo(() => {
    if (!openModal) {
      return undefined;
    }
    const recordId =
      (typeof selectedValuation === 'string' ? selectedValuation : selectedValuation?.record_id) ??
      valuationIdFromUrl;
    const fresh = recordId ? visibleValuations.find(r => r.record_id === recordId) : undefined;
    if (fresh) {
      return fresh;
    }
    if (typeof selectedValuation === 'string') {
      return selectedValuation;
    }
    return pinnedValuation ?? selectedValuation ?? valuationIdFromUrl;
  }, [openModal, pinnedValuation, selectedValuation, valuationIdFromUrl, visibleValuations]);

  // Migrate legacy attio-id to valuation-id silently
  useEffect(() => {
    if (queryParams['attio-id'] && !queryParams['valuation-id']) {
      const migrated: Record<string, string | undefined> = { ...queryParams, 'valuation-id': queryParams['attio-id'] };
      delete migrated['attio-id'];
      navigate({ search: queryString.stringify(migrated) }, { replace: true });
    }
  }, [queryParams, navigate]);

  const handleOpenModal = useCallback((valuation: SimplifiedAttioValuation) => {
    setPinnedValuation(valuation);
    setSelectedValuation(valuation);
    setOpenModal(true);

    const newQueryParams: Record<string, string | undefined> = {
      ...queryParamsRef.current,
      'valuation-id': valuation.record_id,
    };
    delete newQueryParams['attio-id'];
    navigate({ search: queryString.stringify(newQueryParams) }, { replace: true });
  }, [navigate]);

  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
    setSelectedValuation(undefined);
    setPinnedValuation(undefined);

    const newQueryParams: Record<string, string | undefined> = { ...queryParamsRef.current };
    delete newQueryParams['valuation-id'];
    delete newQueryParams['attio-id'];
    navigate({ search: queryString.stringify(newQueryParams) }, { replace: true });
  }, [navigate]);

  // Auto-open modal when valuation-id is in URL and data is loaded
  useEffect(() => {
    if (valuationIdFromUrl && !openModal) {
      setSelectedValuation(valuationIdFromUrl);
      setOpenModal(true);
    }
  }, [valuationIdFromUrl, openModal]);

  const isCalendarView = isCalendarSupportedSection && view === 'calendar';

  const toolbarPrefix = useMemo(
    () => (isCalendarSupportedSection ? <ViewToggle view={view} onChange={setStoredView} /> : undefined),
    [isCalendarSupportedSection, view, setStoredView]
  );

  if (!isSortReady || !isViewReady) {
    return (
      <GenericWorkboard>
        <LoadingSection />
      </GenericWorkboard>
    );
  }

  return (
    <GenericWorkboard>
      <ValuationsTableBulkData
        filters={filters}
        orderBy={orderBy}
        order={order}
        sortCriteria={sortCriteria}
        onRequestSort={handleRequestSort}
        onRowClick={handleOpenModal}
        onVisibleValuationsChange={setVisibleValuations}
        sectionId={sectionId}
        pauseBatchLoading={isCalendarView}
        toolbarPrefix={toolbarPrefix}
        hideTable={isCalendarView}
      />
      {isCalendarView && (
        <Box sx={{ mt: 2 }}>
          <ValuationsCalendarWithToolbar
            filters={filters}
            onSelectEvent={handleOpenModal}
          />
        </Box>
      )}
      <OperationsModal
        open={openModal}
        onClose={handleCloseModal}
        data={operationsModalData}
        isFromModels={false}
      />
    </GenericWorkboard>
  );
};

export default WorkboardDataTable;
